/**
 * Utility class for batching operations using RequestAnimationFrame
 * with improved error handling and cleanup
 */
export class RAFQueue {
  private queue: Set<() => void> = new Set();
  private isProcessing = false;
  private rafId: number | null = null;
  private errorHandler: ((error: Error) => void) | null = null;

  constructor(errorHandler?: (error: Error) => void) {
    this.errorHandler = errorHandler || null;
  }

  /**
   * Add a callback to the queue for processing in the next animation frame
   */
  add(callback: () => void) {
    if (typeof callback !== 'function') {
      console.warn('Invalid callback provided to RAFQueue');
      return;
    }

    this.queue.add(callback);
    this.scheduleProcessing();
  }

  /**
   * Schedule processing of the queue
   */
  private scheduleProcessing() {
    if (this.isProcessing || this.rafId !== null || this.queue.size === 0) return;
    
    try {
      this.isProcessing = true;
      this.rafId = requestAnimationFrame(() => this.processQueue());
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Process all queued callbacks with improved error handling
   */
  private processQueue() {
    if (!this.queue.size) {
      this.cleanup();
      return;
    }

    const callbacks = Array.from(this.queue);
    this.queue.clear();

    callbacks.forEach(callback => {
      try {
        callback();
      } catch (error) {
        this.handleError(error);
      }
    });

    this.cleanup();
    
    // Check if new callbacks were added during processing
    if (this.queue.size > 0) {
      this.scheduleProcessing();
    }
  }

  /**
   * Handle errors in queue processing
   */
  private handleError(error: any) {
    const err = error instanceof Error ? error : new Error(String(error));
    
    if (this.errorHandler) {
      this.errorHandler(err);
    } else {
      console.error('RAFQueue error:', err);
    }
    
    this.cleanup();
  }

  /**
   * Clean up resources and reset state
   */
  private cleanup() {
    this.isProcessing = false;
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  /**
   * Clear all pending callbacks and cancel any scheduled processing
   */
  clear() {
    this.queue.clear();
    this.cleanup();
  }

  /**
   * Clean up resources and remove error handler
   */
  dispose() {
    this.clear();
    this.errorHandler = null;
  }
}